import { useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { fetchPostState } from '@iwoca/lapi-client/edge';
import {
  fetchGetApiIntroducerPortalIntroducerReintroductionSearch,
  fetchPatchApiIntroducerPortalIntroducerState,
  fetchPostApiIntroducerPortalIntroducerCustomerApplication,
  PostApiIntroducerPortalIntroducerCustomerApplicationRequestBody,
} from '@iwoca/lapi-client/introducer_portal';
import { useQuery } from '@tanstack/react-query';
import { useAmplitude } from 'hooks/useAmplitude';
import { useFullStory } from 'hooks/useFullStory/useFullStory';
import { TSupportedProductTypes } from 'lendingApi/applications.types';
import { TPostStateRequestBody } from 'lendingApi/lapiOverrides.types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { PATH_NAMES } from 'utils/routes/pathNames';
import { AMPLITUDE_EVENTS } from 'utils/tracking/tracking.events';
import { v4 } from 'uuid';
import { denormaliseDate } from '../helpers/stateNormalisation';
import {
  TNewSignupFormData,
  TUseFormSubmitParams,
} from './NewSignupForm.types';

const ALLOWED_PRODUCTS: TSupportedProductTypes[] = [
  'credit_facility',
  'flexi24',
];

export const useReintroductionSearch = (
  companyNumber?: string,
  introducerId?: string | null,
  sameIntroducerAccountReintroductions?: boolean,
) => {
  const { data, isFetching: isFetchingReintroductionSearch } = useQuery({
    queryKey: ['reintroduction-search', introducerId, companyNumber],
    queryFn: () =>
      fetchGetApiIntroducerPortalIntroducerReintroductionSearch({
        introducerId: introducerId as string,
        companyNumber: companyNumber as string,
      }),
    enabled: Boolean(
      introducerId && companyNumber && sameIntroducerAccountReintroductions,
    ),
  });

  return {
    isFetchingReintroductionSearch,
    reintroducedPerson: data?.data || null,
  };
};

export const useFormSubmit = ({
  selectedCompany,
  selectedPerson,
  introducerId,
  reintroducedPerson,
}: TUseFormSubmitParams) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [signupError, setSignupError] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { trackEvent } = useAmplitude();
  const { trackFSEvent } = useFullStory();
  const selectedProduct = searchParams.get(
    'selected_product',
  ) as TSupportedProductTypes;

  const buildStateBody = (values: TNewSignupFormData) => {
    if (!selectedCompany || !selectedPerson || !introducerId) {
      return;
    }

    const state: TPostStateRequestBody = {
      data: {
        application: {
          company: {
            registered_company_name: selectedCompany.name,
            company_number: selectedCompany.number,
            type: selectedCompany.company_type,
            trading_from_date: selectedCompany.registration_date,
          },
          people: [
            {
              first_name: selectedPerson.first_name,
              last_name: selectedPerson.last_name,
              uid: v4(),
              roles: ['applicant', 'director', 'guarantor'],
              date_of_birth: denormaliseDate(
                values.dobDay,
                values.dobMonth,
                values.dobYear,
              ),
            },
          ],
        },
        ui: { tip_latest_signup_step: 'new', reintroduction: false },
      },
    };

    if (searchParams.get('__impersonate')) {
      state.data.impersonation = { introducer_id: introducerId };
    }

    return state;
  };

  const onSubmit = async (values?: TNewSignupFormData) => {
    if (
      !selectedCompany ||
      (!selectedPerson && !reintroducedPerson) ||
      (!values && !reintroducedPerson) ||
      !introducerId
    ) {
      return;
    }

    try {
      setIsSubmitting(true);
      setSignupError(null);

      const reintroductionStateKey = reintroducedPerson?.state_key;
      let stateKey = reintroductionStateKey;

      if (!reintroductionStateKey) {
        // If this isn't a reintroduction, create a new state.
        if (!values) {
          /**
           * Shouldn't occur: onSubmit is always called with `values` if there's no `reintroducedPerson`. Adding this to
           * satisfy TypeScript and to Safeguard in the unlikely case there is a `reintroducedPerson` without `state_key`
           */
          throw Error(
            reintroducedPerson
              ? 'Reintroduction customer without state key'
              : 'Missing values and state key',
          );
        }
        const response = await fetchPostState({ body: buildStateBody(values) });
        stateKey = response.data?.state_key;
      }

      if (stateKey) {
        const application: PostApiIntroducerPortalIntroducerCustomerApplicationRequestBody =
          {
            data: {
              // 106 is the source for reintroductions, returning a null
              // leaves the source as just "introducer portal"
              source: reintroducedPerson ? 106 : undefined,
              requested_products: [],
            },
          };

        if (ALLOWED_PRODUCTS.includes(selectedProduct)) {
          application.data.requested_products = [
            { product_type: selectedProduct },
          ];
        }

        const newApplication =
          await fetchPostApiIntroducerPortalIntroducerCustomerApplication({
            introducerId,
            stateKey,
            body: application,
          });

        if (reintroductionStateKey) {
          await fetchPatchApiIntroducerPortalIntroducerState({
            introducerId,
            stateKey: reintroductionStateKey,
            body: {
              data: {
                ui: { tip_latest_signup_step: 'new', reintroduction: true },
              },
            },
          });
        }

        trackEvent(AMPLITUDE_EVENTS.initialApplicationCheckSubmitted, {
          'State key': stateKey,
          Reintroduction: Boolean(reintroductionStateKey),
        });

        if (reintroductionStateKey) {
          trackFSEvent('Reintroduction signup started', { stateKey });
        }

        navigate(
          PATH_NAMES.newApplicationStep1({
            applicationId: newApplication.data.application_id,
          }),
        );
      }
    } catch (e) {
      datadogLogs.logger.error('Failed to create state', {
        createError: e as string,
        introducerID: introducerId,
        isReintroduction: Boolean(reintroducedPerson?.state_key),
      });
      setSignupError(
        'Something went wrong. Please refresh the page and try again.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return { onSubmit, isSubmitting, signupError };
};
